import type { QueryClient } from '@tanstack/react-query';
import { caseDelayKey, disruptionDelaysKey } from '@/lib/delay/keys';
import type { FlightDelayChangedEvent, FlightEscalatedEvent, ICaseFlightDelay } from '@/lib/delay/types';
import { caseExternalPendingKey, caseReassignmentsKey } from './keys';

/**
 * The escalation half of the central WS merge (flight-delay U5) — called from
 * `lib/ws/apply-event.ts` RIGHT AFTER U1's delay merge (`lib/delay/merge.ts`) for the
 * same event. U1 writes what a push carries into the delay caches (phase, kind,
 * `graceUntil`, `releasedAt`, the rows' `flightStatus@v1`); this half covers what a push
 * CANNOT express there, by marking exactly those caches stale — an open surface
 * re-reads once, a closed one only flips stale. Push-driven: nothing here starts a
 * timer or a poll, and nothing is invented from a partial payload.
 *
 *   `FlightEscalated` — the delayed flight became a cancellation:
 *     - a case we read as "no timeline" (`null`) has one now → its timeline;
 *     - a disruption list that does not hold the case (a timeline it never saw) → the list;
 *     - the airline's reassignments start arriving now → the case's reassignments and
 *       open decisions.
 *   `FlightDelayChanged` with an `escalation` — the revision carries the escalation's
 *     COUNTS (`reassigned`, `released`, `conflicts`), while the caches hold the URN
 *     lists, which U1's merge keeps as they were. Where a cached escalation of the same
 *     `seq` disagrees with the counts (S7 listed a conflict; the grace release filled
 *     the three lists) — or the cache knows no escalation at all — that timeline is
 *     re-read, and so are the case's reassignments and open decisions (the badges).
 */

/** The cached escalation of `d` disagrees with the push's counts (or is missing / another seq). */
function escalationStale(
  d: ICaseFlightDelay,
  e: NonNullable<FlightDelayChangedEvent['payload']['escalation']>,
): boolean {
  const c = d.escalation;
  if (!c || c.seq !== e.seq) return true;
  return (
    (c.reassignedSubCaseUrns?.length ?? 0) !== e.reassigned ||
    (c.releasedSubCaseUrns?.length ?? 0) !== e.released ||
    (c.conflictSubCaseUrns?.length ?? 0) !== e.conflicts
  );
}

function invalidateExact(qc: QueryClient, key: readonly unknown[]): void {
  void qc.invalidateQueries({ queryKey: key, exact: true });
}

/** The case's badges (reassignments + open external decisions) — an open list re-reads once. */
function invalidateBadges(qc: QueryClient, caseUrn: string): void {
  invalidateExact(qc, caseReassignmentsKey(caseUrn));
  invalidateExact(qc, caseExternalPendingKey(caseUrn));
}

/** `FlightEscalated@v1`, after U1's `applyFlightEscalated`. See the module comment. */
export function applyEscalationArrived(qc: QueryClient, event: FlightEscalatedEvent): void {
  const caseKey = caseDelayKey(event.caseUrn);
  if (qc.getQueryData<ICaseFlightDelay | null>(caseKey) === null) invalidateExact(qc, caseKey);

  const listKey = disruptionDelaysKey(event.disruptionUrn);
  const list = qc.getQueryData<ICaseFlightDelay[]>(listKey);
  if (list !== undefined && !list.some((d) => d.caseUrn === event.caseUrn)) invalidateExact(qc, listKey);

  invalidateBadges(qc, event.caseUrn);
}

/** `FlightDelayChanged@v1`, after U1's `applyFlightDelayChanged`. See the module comment. */
export function applyEscalationProgress(qc: QueryClient, event: FlightDelayChangedEvent): void {
  const e = event.payload.escalation;
  if (!e || typeof e.seq !== 'number') return;
  let stale = false;

  const caseKey = caseDelayKey(event.caseUrn);
  const cached = qc.getQueryData<ICaseFlightDelay | null>(caseKey);
  if (cached && escalationStale(cached, e)) {
    stale = true;
    invalidateExact(qc, caseKey);
  }

  const listKey = disruptionDelaysKey(event.disruptionUrn);
  const entry = qc.getQueryData<ICaseFlightDelay[]>(listKey)?.find((d) => d.caseUrn === event.caseUrn);
  if (entry && escalationStale(entry, e)) {
    stale = true;
    invalidateExact(qc, listKey);
  }

  // Nothing cached to compare with: the counts may still have moved — the badges re-read
  // only if open (an inactive query just flips stale).
  if (stale || (!cached && !entry)) invalidateBadges(qc, event.caseUrn);
}
